import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Input, Typography, Divider, Row, Col, Breadcrumb, message, Modal, Dropdown, Checkbox } from "antd";
import { InfoCircleOutlined, MinusOutlined, PlusOutlined, CarOutlined, ReloadOutlined } from "@ant-design/icons";
import { FaHeart, FaPlus } from "react-icons/fa";
import { TbDotsVertical } from "react-icons/tb";
import { Link, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useApiCall } from "../services/api";
import { useGlobalModal } from "../context/SuccessModalProvider";
import { useDispatch, useSelector } from "react-redux";
import { disclaimerItems, DYED_TYPES, estimatedDispatchTimes, productsMenuItems, roleIds } from "../constant";
import FileUploadModal from "../components/models/FileUploadModal";
import { triggerRefresh } from "../redux/auth/cartRefreshSlice";
import PrimaryButton from "../components/Forms/PrimaryButton";
import { DropdownMenuItems } from "./Products";
import CustomBadge, { CustomDot, badgeVariantStyles } from "../components/Charts/CustomBadge";
import { setCounts } from "../redux/auth/navCountSlice";
import {
  availibilityStatusMap,
  calcHasGrey,
  calcInStock,
  calcTotalGrey,
  calcTotalInStock,
  getStockStatus,
  handleRouteNav,
  inchesToCm,
  roundDecimal,
  showAPC,
} from "../utils";
import ProductImageName from "../components/Layout/ProductImageName";
import { PiHeartFill } from "react-icons/pi";
import SearchInput from "../components/Forms/SearchInput";

const { Title, Text } = Typography;
const { TextArea } = Input;

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const ProductDetails = () => {
  const { postApi, loading } = useApiCall();
  const dispatch = useDispatch();
  const { sort_no } = useParams();
  const { showPopup } = useGlobalModal();
  const { user_id, full_name, role_id, is_customer, customer_id } = useSelector((state) => state.auth);
  const location = useLocation();
  const { cart_id, shade_no, order_cases_no, order_item_dyed_type, comments: itemComments, apc_no, cart_item_tdlevel_code } = location.state || {};

  const [orderSplitModal, setOrderSplitModal] = useState(false);
  const [shadesModal, setShadesModal] = useState(false);
  const [modalData, setModalData] = useState({});

  const [pdProductData, setPdProductData] = useState({});
  const [tdProductData, setTdProductData] = useState({});
  const [productData, setProductData] = useState({});
  const [tdMapping, setTdMapping] = useState({});
  const [selectedTdData, setSelectedTdData] = useState({
    tdlevelcode: "",
    tdlevelname: "",
  });
  const [itemsList, setItemsList] = useState([]);
  const [filteredItemsList, setFilteredItemsList] = useState([]);
  const [pdItemsList, setPdItemsList] = useState([]);
  const [tdItemsList, setTdItemsList] = useState([]);

  const [isPDSelected, setIsPDSelected] = useState(order_item_dyed_type ? order_item_dyed_type === DYED_TYPES.piece : true);
  const [selectedShadeIndex, setSelectedShadeIndex] = useState(0);
  const [stockTab, setStockTab] = useState("in_stock");

  const [noOfCases, setNoOfCases] = useState(2);
  const [comments, setComments] = useState(itemComments || "");
  const [cartItems, setCartItems] = useState([]);
  const [cartItem, setCartItem] = useState({});
  const { collapsed } = useSelector((state) => state.navCounts);

  const [currentItem, setCurrentItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [apcChecked, setApcChecked] = useState(apc_no ? true : false);
  const [apcModal, setApcModal] = useState(false);
  const [quantityWarningModal, setQuantityWarningModal] = useState(false);
  const [wishlistState, setWishlistState] = useState({
    pd_tag: "no",
    td_tag: "no",
    pd_count: 0,
    td_count: 0,
  });

  const selectedType = isPDSelected ? "piece" : "top";
  const navigate = useNavigate();

  const clampAndNormalizeCases = (n, tempIsPDSelected) => {
    let val = Math.max(0, toNumber(n, 0));
    if (tempIsPDSelected ?? isPDSelected) {
      val = Math.max(2, val);
      if (val % 2 === 1) val = val + 1;
    } else {
      val = Math.max(1, val);
    }
    return val;
  };

  const handleCheckout = () => {
    navigate(handleRouteNav("/product-catalog/checkout", role_id));
  };

  const fetchProductData = async () => {
    try {
      const response = await postApi(is_customer ? "product/product-detail" : "sales/product/product-details", {
        sort_no,
        customer_id: is_customer ? customer_id : undefined,
      });
      if (response.status) {
        const isPd = response.data?.dyed_type === DYED_TYPES.piece;

        setIsPDSelected(isPd);

        // NOTE: May not be required as the order item dyed type is set as the default selected type on page load
        // else if (shade_no) {
        //   setIsPDSelected(order_item_dyed_type === DYED_TYPES.piece);
        // }

        let wishListData = wishlistState;

        if (isPd) {
          setPdProductData(response.data);
          wishListData.pd_tag = response.data?.product?.wishlist_tag || "no";
          wishListData.pd_count = response.data?.product?.wishlist_count || 0;
        } else {
          setTdProductData(response.data);
          wishListData.td_tag = response.data?.product?.wishlist_tag || "no";
          wishListData.td_count = response.data?.product?.wishlist_count || 0;
        }
        if (response.data?.product?.sort_no_linked) {
          const linkedResponse = await postApi(is_customer ? "product/product-detail" : "sales/product/product-details", {
            sort_no: response.data?.product?.sort_no_linked,
            customer_id: is_customer ? customer_id : undefined,
          });
          if (linkedResponse.status) {
            if (isPd) {
              setTdProductData(linkedResponse.data);
              wishListData.td_tag = linkedResponse.data?.product?.wishlist_tag || "no";
              wishListData.td_count = linkedResponse.data?.product?.wishlist_count || 0;
            } else {
              setPdProductData(linkedResponse.data);
              wishListData.pd_tag = linkedResponse.data?.product?.wishlist_tag || "no";
              wishListData.pd_count = linkedResponse.data?.product?.wishlist_count || 0;
            }
          }
        }
        setProductData(response.data);
        setWishlistState(wishListData);

        const { tdlevelname_options = "", tdlevelcode_options = "", tdlevel_code = "", tdlevel_name = "" } = response.data || {};

        const tdNames = tdlevelname_options?.split(",")?.map((n) => n.trim()) || [];
        const tdCodes = tdlevelcode_options?.split(",")?.map((c) => c.trim()) || [];

        const mapping = {};
        if (tdNames.length === tdCodes.length && tdCodes[0] !== "") {
          tdCodes.forEach((code, idx) => {
            mapping[code] = tdNames[idx];
          });
        }

        console.log("mapping", mapping);

        setTdMapping(mapping);

        let targetCode = tdlevel_code || "";
        let targetName = tdlevel_name || "";

        if (cart_item_tdlevel_code && mapping[cart_item_tdlevel_code]) {
          targetCode = cart_item_tdlevel_code;
          targetName = mapping[cart_item_tdlevel_code];
        }

        setSelectedTdData({
          tdlevelcode: targetCode,
          tdlevelname: targetName,
        });
      } else {
        navigate(handleRouteNav("/product-catalog", role_id));
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchProductData();
  }, []);

  useEffect(() => {
    if (!shadesModal) {
      setSearchValue("");
    }
  }, [shadesModal]);

  useEffect(() => {
    const fetchCartItem = async () => {
      if (!cart_id) return;

      try {
        const res = await postApi(is_customer ? "cart/get-single-item" : "sales/cart/get-single-item", { cart_id });
        if (res?.status && res?.data) {
          const item = res.data;
          setCartItem(item);
          if (item.comments) setComments(item.comments);
          setApcChecked(!!item.apc_no);
        } else {
          navigate(handleRouteNav("/product-catalog", role_id));
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchCartItem();
  }, [cart_id]);

  const getShadeStockCases = async (forceTab = null) => {
    try {
      const res = await postApi("product/get-shade-stock-cases", {
        sort_no: productData.sort_no,
      });
      if (res?.status && Array.isArray(res.data)) {
        const list = res.data.filter((e) => e?.shade && e?.shade?.detail != null);
        setItemsList(list);
        setPdItemsList(list.filter((e) => e?.dyed_type === DYED_TYPES.piece));
        setTdItemsList(list.filter((e) => e?.dyed_type === DYED_TYPES.top));

        // if (!cart_id) {
        //   let tempPdSelected = isPDSelected;
        //   if (shade_no) {
        //     tempPdSelected = order_item_dyed_type === DYED_TYPES.piece;
        //   } else {
        //     tempPdSelected = list.some((e) => e.dyed_type === DYED_TYPES.piece);
        //   }
        // onPdTdTap(tempPdSelected, list, forceTab);
        // setIsPDSelected(tempPdSelected);
        // }
      } else {
        setItemsList([]);
        setFilteredItemsList([]);
      }
    } catch (e) {
      console.error(e);
      setItemsList([]);
      setFilteredItemsList([]);
    }
  };

  useEffect(() => {
    if (productData && Object.keys(productData).length > 0) {
      getShadeStockCases();

      // this breaks when I reorder and change to another dyed type, then navigate back and then renavigate forward again
      // const newUrl = handleRouteNav(
      //   `/product-details/${productData.sort_no}`,
      //   role_id,
      // );
      // window.history.replaceState(window.history.state "", newUrl);
    }
  }, [productData]);

  const fetchCart = async () => {
    try {
      const payload = {
        customer_id: customer_id,
      };
      const res = await postApi(is_customer ? "cart/get-cart-customer-id" : "sales/cart/get-cart-user-id", is_customer ? payload : { user_id });
      if (res?.status && Array.isArray(res.data)) {
        setCartItems(res.data);
        dispatch(setCounts({ cartItems: res?.total_items || 0 }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [user_id]);

  const handleTabChange = (newTab) => {
    setStockTab(newTab);
    onPdTdTap(isPDSelected, itemsList, newTab);
  };

  const onShadeSelected = (idx) => {
    setSelectedShadeIndex(idx);
    setShadesModal(false);
  };

  const minusNoOfCases = () => {
    let next = noOfCases - (isPDSelected ? 2 : 1);
    next = clampAndNormalizeCases(next);
    setNoOfCases(next);
  };

  const plusNoOfCases = () => {
    let next = noOfCases + (isPDSelected ? 2 : 1);
    next = clampAndNormalizeCases(next);
    setNoOfCases(next);
  };

  const occupiedKeys = useMemo(() => {
    const keys = new Set();
    cartItems.forEach((item) => {
      if (cart_id && String(item.cart_id) === String(cart_id)) return;
      ["cases_no", "folding_barcode", "grey_pcsno"].forEach((field) => {
        (item[field] || "").split(",").forEach((k) => {
          if (k.trim()) keys.add(k.trim());
        });
      });
    });
    return keys;
  }, [cartItems, cart_id]);

  const { showInStockTab, showGreyTab } = useMemo(() => {
    const currentDyedList = (itemsList || []).filter((e) => e?.shade && String(e.dyed_type) === (isPDSelected ? DYED_TYPES.piece : DYED_TYPES.top));

    if (currentDyedList.length === 0) {
      return { showInStockTab: true, showGreyTab: true };
    }

    return {
      showInStockTab: currentDyedList.some((e) => calcInStock(e, { occupiedKeys })),
      showGreyTab: currentDyedList.some((e) => calcHasGrey(e, { occupiedKeys })),
    };
  }, [itemsList, isPDSelected, occupiedKeys]);

  const stockCalculations = useMemo(() => {
    const shade = filteredItemsList[selectedShadeIndex];
    const slabMtrs = Number(shade?.dcl_slabmtrs) || 250;
    const casesNeeded = clampAndNormalizeCases(noOfCases);

    if (!shade) {
      const mtoMeters = casesNeeded * slabMtrs;
      return {
        totalMtrs: mtoMeters,
        inStockQty: 0,
        foldingQty: 0,
        greyQty: 0,
        mtoCases: casesNeeded,
        casesInStock: 0,
        casesInFolding: 0,
        casesInGrey: 0,
        bsrUsedList: [],
        foldingUsedList: [],
        greyUsedList: [],
      };
    }

    const bsrItems = (shade.bsr_stock_cases || []).filter((e) => !occupiedKeys.has(String(e.caseno)));
    const foldingItems = (shade.folding_stock || []).filter((e) => !occupiedKeys.has(String(e.bar_code)));
    const greyItems = (shade.grey_stock || []).filter((e) => !occupiedKeys.has(String(e.pcsno)));

    let totalMtrs = 0;
    let inStockQty = 0;
    let foldingQty = 0;
    let greyQty = 0;
    let casesInStock = 0;
    let casesInFolding = 0;
    let casesInGrey = 0;

    let bsrUsedList = [];
    let foldingUsedList = [];
    let greyUsedList = [];

    if (stockTab === "in_stock") {
      const bsrUsed = Math.min(casesNeeded, bsrItems.length);
      for (let i = 0; i < bsrUsed; i++) {
        const qty = Number(bsrItems[i].stockqty || 0);
        totalMtrs += qty;
        inStockQty += qty;
        bsrUsedList.push(bsrItems[i]);
      }
      casesInStock = bsrUsed;
      let remaining = casesNeeded - bsrUsed;

      if (remaining > 0 && foldingItems.length > 0) {
        const totalFoldingMtrsAvail = foldingItems.reduce((acc, e) => acc + Number(e.graded_mtrs || 0), 0);
        const totalFoldingCasesAvail = Math.ceil(totalFoldingMtrsAvail / slabMtrs);
        const foldingUsed = Math.min(remaining, totalFoldingCasesAvail);

        if (foldingUsed > 0) {
          if (foldingUsed >= totalFoldingCasesAvail) {
            foldingQty = totalFoldingMtrsAvail;
            foldingUsedList = [...foldingItems];
          } else {
            const mtrsBudget = foldingUsed * slabMtrs;
            let acc = 0;

            for (let item of foldingItems) {
              const pieceMtrs = Number(item.graded_mtrs || 0);

              // If there is only one folding item and it meets/exceeds one slab,
              // keep it instead of skipping it just because it overshoots the remaining budget.
              if (acc + pieceMtrs > mtrsBudget && pieceMtrs > slabMtrs) {
                foldingUsedList.push(item);
                acc += pieceMtrs;
                break;
              }

              if (acc + pieceMtrs > mtrsBudget) {
                break;
              }

              foldingUsedList.push(item);
              acc += pieceMtrs;
            }
            foldingQty = acc;
          }
          totalMtrs += foldingQty;
          casesInFolding = foldingUsed;
          remaining -= foldingUsed;
        }
      }
      var mtoCases = remaining;
    } else if (stockTab === "grey") {
      if (casesNeeded > 0 && greyItems.length > 0) {
        const totalGreyMtrsAvail = greyItems.reduce((acc, e) => acc + Number(e.dcl_mtrs || 0) * 0.95, 0);

        const totalGreyCasesAvail = Math.ceil(totalGreyMtrsAvail / slabMtrs);
        const greyUsed = Math.min(casesNeeded, totalGreyCasesAvail);

        if (greyUsed > 0) {
          if (greyUsed >= totalGreyCasesAvail) {
            greyQty = totalGreyMtrsAvail;
            greyUsedList = [...greyItems];
          } else {
            const mtrsBudget = greyUsed * slabMtrs;
            let acc = 0;

            for (let item of greyItems) {
              const rawMtrs = Number(item.dcl_mtrs || 0);
              const pieceMtrs = rawMtrs * 0.95;

              // This will be needed here as well
              if (acc + pieceMtrs > mtrsBudget && pieceMtrs > slabMtrs) {
                greyUsedList.push(item);
                acc += pieceMtrs;
                break;
              }

              if (acc + pieceMtrs > mtrsBudget) {
                break;
              }

              greyUsedList.push(item);
              acc += pieceMtrs;
            }
            greyQty = acc;
          }

          totalMtrs += greyQty;
          casesInGrey = greyUsed;
        }
      }

      var mtoCases = casesNeeded - casesInGrey;
    } else {
      var mtoCases = casesNeeded;
    }

    if (mtoCases > 0) totalMtrs += mtoCases * slabMtrs;

    const selectedShadeNo = String(shade?.descr_shade || shade?.shade_no || "");
    const activeAvailableCases = stockTab === "grey" ? casesInGrey : casesInStock + casesInFolding;

    let stockStatus = {};
    if (stockTab === "in_stock") {
      stockStatus = availibilityStatusMap.IN_STOCK;
    } else if (stockTab === "grey") {
      stockStatus = availibilityStatusMap.GREY;
    } else {
      stockStatus = availibilityStatusMap.MTO;
    }

    return {
      shade,
      selectedShadeNo,
      activeAvailableCases,
      totalMtrs: roundDecimal(totalMtrs),
      inStockQty: roundDecimal(inStockQty),
      foldingQty: roundDecimal(foldingQty),
      greyQty: roundDecimal(greyQty),
      mtoQty: roundDecimal(mtoCases * slabMtrs),
      mtoCases,
      casesInStock,
      casesInFolding,
      casesInGrey,
      bsrUsedList,
      foldingUsedList,
      greyUsedList,
      stockStatus,
      totalGreyQty: roundDecimal(calcTotalGrey(shade, occupiedKeys) * 0.95),
      totalInStockQty: calcTotalInStock(shade, occupiedKeys),
    };
  }, [noOfCases, selectedShadeIndex, stockTab, filteredItemsList, occupiedKeys]);

  const onTapCallRef = useRef(0);

  const onPdTdTap = (value, sourceList = itemsList, targetTab = null) => {
    onTapCallRef.current += 1;
    console.log("onTapCall:", onTapCallRef.current);
    const targetDyeType = value ? DYED_TYPES.piece : DYED_TYPES.top;

    const baseFiltered = (sourceList || []).filter((e) => e?.shade && String(e.dyed_type) === targetDyeType);

    if (baseFiltered.length === 0) {
      setStockTab("mto");
      setIsPDSelected(value);
      setFilteredItemsList([]);
      setSelectedShadeIndex(0);
      setNoOfCases(() => clampAndNormalizeCases(0, value));
      if (!cart_id && !shade_no) setApcChecked(false);
      return;
    }

    let desiredShadeName = null;
    let isExplicitShade = false;

    if (cart_id && cartItem?.shade_name && cartItem.dye_type === targetDyeType) {
      desiredShadeName = cartItem.shade_name;
      isExplicitShade = true;
    } else if (shade_no && order_item_dyed_type === targetDyeType) {
      desiredShadeName = shade_no;
      isExplicitShade = true;
    } else if (targetTab && filteredItemsList[selectedShadeIndex]) {
      // NOTE: Uncomment to preserve shade on tab switch
      // desiredShadeName = filteredItemsList[selectedShadeIndex]?.shade?.descr;
    }

    const anyInStock = baseFiltered.some((e) => calcInStock(e, { occupiedKeys }));
    const anyInGrey = value ? baseFiltered.some((e) => calcHasGrey(e, { occupiedKeys })) : false;

    const resolveBestTab = () => {
      // Priority A: User explicitly clicked a tab (Validate it against actual inventory)
      if (targetTab && targetTab !== stockTab) {
        if (targetTab === "in_stock" && !anyInStock) return anyInGrey ? "grey" : "mto";
        if (targetTab === "grey" && !anyInGrey) return anyInStock ? "in_stock" : "mto";
        return targetTab;
      }

      // Priority B: Resolving Explicit Shade state (Cart Item OR Route Param)
      if (isExplicitShade && desiredShadeName) {
        const targetShadeObj = baseFiltered.find((e) => String(e?.shade?.descr) === String(desiredShadeName));

        if (targetShadeObj) {
          if (cart_id && cartItem) {
            const hasStock = Number(cartItem.cases_in_stock || 0) > 0 || Number(cartItem.cases_in_folding || 0) > 0;
            const hasGrey = value ? Number(cartItem.cases_in_grey || 0) > 0 : false;

            if (hasStock && calcInStock(targetShadeObj, { occupiedKeys })) return "in_stock";

            if (hasGrey && calcHasGrey(targetShadeObj, { occupiedKeys })) return "grey";
          }

          if (calcInStock(targetShadeObj, { occupiedKeys })) return "in_stock";
          if (value && calcHasGrey(targetShadeObj, { occupiedKeys })) return "grey";

          return "mto";
        }
      }

      // Priority C: Default Initialization (No specific shade)
      return anyInStock ? "in_stock" : anyInGrey ? "grey" : "mto";
    };

    const finalTab = resolveBestTab();

    let finalFilteredList = baseFiltered;
    if (finalTab === "in_stock") {
      finalFilteredList = baseFiltered.filter((e) => calcInStock(e, { occupiedKeys }));
    } else if (finalTab === "grey") {
      finalFilteredList = baseFiltered.filter((e) => calcHasGrey(e, { occupiedKeys }));
    }

    if (finalFilteredList.length === 0) {
      finalFilteredList = baseFiltered;
      finalTab = "mto";
    }

    let nextSelectedIndex = 0;
    if (desiredShadeName) {
      const foundIndex = finalFilteredList.findIndex((e) => String(e?.shade?.descr) === String(desiredShadeName));
      if (foundIndex !== -1) nextSelectedIndex = foundIndex;
    }

    setNoOfCases((prevCases) => {
      if (targetTab && targetTab !== stockTab) return clampAndNormalizeCases(prevCases, value);

      let resolvedCases = value ? 2 : 1;

      if (cart_id && cartItem && cartItem.dye_type === targetDyeType) {
        resolvedCases = cartItem.cases || resolvedCases;
      } else if (shade_no && order_item_dyed_type === targetDyeType) {
        resolvedCases = order_cases_no || resolvedCases;
      }

      return clampAndNormalizeCases(resolvedCases, value);
    });

    setApcChecked((prev) =>
      cart_id && cartItem && cartItem.dye_type === targetDyeType
        ? !!cartItem.apc_no
        : shade_no && order_item_dyed_type === targetDyeType
          ? !!apc_no
          : false,
    );

    setStockTab(finalTab);
    setIsPDSelected(value);
    setFilteredItemsList(finalFilteredList);
    setSelectedShadeIndex(nextSelectedIndex);
  };

  // const onLinkedProductTap = (value) => {
  //   setIsPDSelected(value);
  //   setItemsList([]);

  //   const targetProduct = value ? pdProductData : tdProductData;
  //   setProductData(targetProduct);

  //   if (cart_id && cartItem && targetProduct.dyed_type === cartItem.dye_type) {
  //     setComments(cartItem.comments || "");
  //   } else if (shade_no && targetProduct.dyed_type === order_item_dyed_type) {
  //     setComments(itemComments || "");
  //   } else {
  //     setComments("");
  //   }

  //   // TODO: reset the trade level as done
  //   if (targetProduct) {
  //     const tdNames = targetProduct?.tdlevelname_options?.split(",")?.map((n) => n.trim());
  //     const tdCodes = targetProduct?.tdlevelcode_options?.split(",")?.map((c) => c.trim());

  //     // TODO: optimize
  //     if (tdNames && tdCodes && tdNames.length === tdCodes.length) {
  //       const mapping = {};
  //       tdCodes.forEach((code, idx) => {
  //         mapping[code] = tdNames[idx];
  //       });

  //       // TODO: add this check based on dyed type (same as the above check for setting comments)
  //       // if (mapping[cart_item_tdlevel_code || response.data?.tdlevel_code]) {
  //       //     setSelectedTdData({
  //       //       tdlevelcode: cart_item_tdlevel_code || response.data?.tdlevel_code || "",
  //       //       tdlevelname: mapping[cart_item_tdlevel_code || response.data?.tdlevel_code] || "",
  //       //     });
  //       //   } else {
  //       //     setSelectedTdData({
  //       //       tdlevelcode: response.data?.tdlevel_code || "",
  //       //       tdlevelname: response.data?.tdlevel_name || "",
  //       //     });
  //       //   }

  //       setTdMapping(mapping);
  //       setSelectedTdData(() => ({
  //         tdlevelcode: targetProduct.tdlevel_code || "",
  //         tdlevelname: mapping[targetProduct.tdlevel_code] || targetProduct.tdlevel_name || "",
  //       }));
  //     } else {
  //       setTdMapping({});
  //       setSelectedTdData({
  //         tdlevelcode: targetProduct.tdlevel_code || "",
  //         tdlevelname: targetProduct.tdlevel_name || "",
  //       });
  //     }
  //   }
  // };

  const onLinkedProductTap = (value) => {
    setIsPDSelected(value);
    setItemsList([]);

    const targetProduct = value ? pdProductData : tdProductData;
    setProductData(targetProduct);

    const isSameDyeType =
      (cart_id && cartItem && targetProduct?.dyed_type === cartItem.dye_type) || (shade_no && targetProduct?.dyed_type === order_item_dyed_type);

    setComments(isSameDyeType ? cartItem?.comments || itemComments || "" : "");

    const {
      tdlevelname_options = "",
      tdlevelcode_options = "",
      tdlevel_code: prodDefaultCode = "",
      tdlevel_name: prodDefaultName = "",
    } = targetProduct || {};

    const tdNames = tdlevelname_options?.split(",")?.map((n) => n.trim()) || [];
    const tdCodes = tdlevelcode_options?.split(",")?.map((c) => c.trim()) || [];

    const mapping = {};
    if (tdNames.length === tdCodes.length && tdCodes[0] !== "") {
      tdCodes.forEach((code, idx) => {
        mapping[code] = tdNames[idx];
      });
    }
    setTdMapping(mapping);

    let targetCode = prodDefaultCode;
    let targetName = prodDefaultName;

    if (isSameDyeType && cart_item_tdlevel_code && mapping[cart_item_tdlevel_code]) {
      targetCode = cart_item_tdlevel_code;
      targetName = mapping[cart_item_tdlevel_code];
    }

    setSelectedTdData({
      tdlevelcode: targetCode,
      tdlevelname: targetName,
    });
  };

  const hasInitializedCart = useRef(false);
  const skipNextStockUpdate = useRef(false);

  // useEffect(() => {
  //   if (cart_id && Object.keys(cartItem).length > 0 && itemsList.length > 0 && !hasInitializedCart.current) {
  //     const pdSelected = cartItem.dye_type !== DYED_TYPES.top;
  //     onPdTdTap(pdSelected, itemsList, null);
  //     hasInitializedCart.current = true;
  //   }
  // }, [cartItem, itemsList]);

  // useEffect(() => {
  //   if (itemsList.length === 0) return;

  //   if (cart_id && !hasInitializedCart.current) {
  //     return;
  //   }

  //   if (skipNextStockUpdate.current) {
  //     setTimeout(() => {
  //       skipNextStockUpdate.current = false;
  //     }, 300);
  //     return;
  //   }

  //   const timer = setTimeout(() => {
  //     onPdTdTap(isPDSelected, itemsList, stockTab);
  //   }, 50);

  //   return () => clearTimeout(timer);
  // }, [occupiedKeys, itemsList]);

  useEffect(() => {
    if (itemsList.length === 0) return;

    if (cart_id && Object.keys(cartItem).length === 0) return;

    if (cart_id && Object.keys(cartItem).length > 0 && !hasInitializedCart.current) {
      const pdSelected = cartItem.dye_type !== DYED_TYPES.top;
      onPdTdTap(pdSelected, itemsList, null);
      hasInitializedCart.current = true;
      return;
    }

    if (skipNextStockUpdate.current) {
      setTimeout(() => {
        skipNextStockUpdate.current = false;
      }, 300);
      return;
    }

    const timer = setTimeout(() => {
      onPdTdTap(isPDSelected, itemsList, stockTab);
    }, 50);
    return () => clearTimeout(timer);
  }, [itemsList, occupiedKeys, cartItem]);

  useEffect(() => {
    if (itemsList.length === 0) return;
    if (skipNextStockUpdate.current) return;

    if (stockTab === "grey" && !showGreyTab) {
      handleTabChange(showInStockTab ? "in_stock" : "mto");
    } else if (stockTab === "in_stock" && !showInStockTab) {
      handleTabChange(isPDSelected && showGreyTab ? "grey" : "mto");
    }
  }, [showGreyTab, showInStockTab]);

  const handleAddToCart = async (e, approved = false) => {
    // TODO: Show this modal if the quantity is less than 180
    // title: Alert!
    // content: The quantity you are trying to order is too less. Please add more cases.

    try {
      const {
        shade,
        selectedShadeNo,
        activeAvailableCases,
        totalMtrs,
        inStockQty,
        foldingQty,
        greyQty,
        mtoQty,
        mtoCases,
        casesInStock,
        casesInFolding,
        casesInGrey,
        bsrUsedList,
        foldingUsedList,
        greyUsedList,
      } = stockCalculations;

      if (totalMtrs < 180) {
        return setQuantityWarningModal(true);
      } else if (!productData?.product?.product_id) {
        return showPopup({
          title: null,
          content: <span className="custom-modal-title">Add product image first!</span>,
          onOk: () => console.log("Confirmed!"),
          className: "custom-success-modal",
        });
      }
      if (!productData || filteredItemsList.length === 0 || !Number(stockCalculations.totalMtrs)) return;

      await new Promise((resolve) => setTimeout(resolve, 300));

      if (mtoCases > 0 && !approved && activeAvailableCases > 0) {
        setModalData({
          shade,
          casesInStock: activeAvailableCases,
          makeToOrder: mtoCases,
        });
        setOrderSplitModal(true);
        return;
      }

      const payload = {
        sort_no: productData?.product?.sort_no,
        basic_sortno: productData?.basic_sortno,
        fabric_type: productData?.fabric_type,
        user_id: is_customer ? 0 : user_id,
        cart_id,
        product_id: productData?.product?.product_id,
        quality_code: String(productData?.quality_code || ""),
        clienttype: productData?.clienttype,
        short_name: productData?.short_name || "-",
        product_picture: shade?.shade?.detail?.shade_picture_url?.split("/")?.pop() || "",
        price_per_meter: productData?.rate,
        fabric_specs: `${productData?.finish_width || ""}, ${productData?.finish_wt || ""}, ${productData?.blend_name || ""}`,
        dye_type: shade?.dyed_type || "-",
        shade_no: selectedShadeNo,
        shade_name: String(shade?.descr_shade || ""),

        cases_id: bsrUsedList.map((e) => e.stock_id).join(","),
        cases_no: bsrUsedList.map((e) => e.caseno).join(","),

        folding_id: foldingUsedList.map((e) => e.folding_stock_id).join(",") || null,
        folding_barcode: foldingUsedList.map((e) => e.bar_code).join(",") || null,

        grey_id: greyUsedList.map((e) => e.grey_stock_id).join(",") || null,
        grey_pcsno: greyUsedList.map((e) => e.pcsno).join(",") || null,

        cases: String(clampAndNormalizeCases(noOfCases)),
        cases_in_stock: String(casesInStock),
        cases_in_folding: String(casesInFolding),
        cases_in_grey: String(casesInGrey),
        cases_make_to_order: String(mtoCases),
        total_quantity: totalMtrs,
        total_in_stock_quantity: inStockQty,
        total_folding_quantity: foldingQty,

        total_grey_quantity: greyQty,
        total_make_to_order_quantity: mtoQty,

        comments: comments?.trim(),
        tdlevel_code: selectedTdData.tdlevelcode || productData.tdlevel_code,
        tdlevel_name: selectedTdData.tdlevelname || productData.tdlevel_name,
        customer_id: is_customer ? customer_id : undefined,
      };

      if (["grey", "mto"].includes(stockTab) && apcChecked) {
        payload.apc_no = "APC";
      } else {
        payload.apc_no = null;
      }

      const addUrl = is_customer ? "cart/add-to-cart" : "sales/cart/add-to-cart";
      const updateUrl = is_customer ? "cart/update-cart" : "sales/cart/update-cart";

      const res = await postApi(cart_id ? updateUrl : addUrl, payload, true, null, () => {
        if (cart_id) {
          navigate(handleRouteNav("/product-catalog/checkout", role_id));
        }
      });

      if (res?.status) {
        if (cart_id && cartItem) {
          skipNextStockUpdate.current = true;
        }
        setComments((prev) => (cart_id && cartItem ? prev : ""));
        setNoOfCases((prev) => (cart_id && cartItem ? prev : isPDSelected ? 2 : 1));
        fetchCart();
        setOrderSplitModal(false);
        getShadeStockCases();
        setSelectedTdData((prev) =>
          cart_id && cartItem ? prev : { tdlevelcode: productData.tdlevel_code || "", tdlevelname: productData.tdlevel_name || "" },
        );
        if (!shade_no) {
          setApcChecked((prev) => (cart_id && cartItem ? prev : false));
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpload = async (files) => {
    if (!files?.length) {
      showPopup({
        title: null,
        content: <span className="custom-modal-title">Please select an image file to upload.</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return;
    }
    const file = files[0]?.originFileObj || files[0];
    if (file.type === "image/jpg") file.type = "image/jpeg";

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      showPopup({
        title: null,
        content: <span className="custom-modal-title">Please upload only JPG or PNG files</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return;
    }

    const formData = new FormData();
    formData.append("quality_code", String(currentItem?.quality_code || ""));
    formData.append("sort_no", String(currentItem?.sort_no || ""));
    formData.append("updated_by", String(full_name || ""));
    formData.append("updated_by_id", String(user_id || ""));
    formData.append("product_pic", file);
    try {
      const response = await postApi("sales/product/update", formData, true);
      setIsModalOpen(false);
      fetchProductData();
    } catch (error) {
      console.error(error);
    }
  };

  const shadeChips = (sliced = true, searchValue) => {
    const visibleShades = 4;
    const selectedShade = filteredItemsList[selectedShadeIndex];
    const rearrangedList =
      selectedShadeIndex > visibleShades - 1 && sliced
        ? [selectedShade, ...filteredItemsList.filter((_, index) => index !== selectedShadeIndex)]
        : filteredItemsList;
    const finalShades = sliced
      ? rearrangedList.slice(0, visibleShades)
      : rearrangedList.filter((shade) => shade?.descr_shade?.toLowerCase().includes(searchValue ? searchValue.toLowerCase() : ""));
    const showMore = sliced && rearrangedList.length > visibleShades;

    return (
      <>
        <div className={`flex flex-wrap gap-3  ${sliced ? "" : ""}`}>
          {finalShades.length > 0 ? (
            <>
              {finalShades?.map((shade, idx) => {
                const isSelected = shade === selectedShade;
                return (
                  <div
                    key={idx}
                    className="flex flex-col items-center cursor-pointer"
                    onClick={() => onShadeSelected(filteredItemsList.indexOf(shade))}
                  >
                    <div
                      className={`w-16 h-16 rounded-xl relative overflow-hidden flex items-center justify-center transition-all duration-300 transform hover:scale-105 ${
                        isSelected ? "ring-2 ring-red-700 ring-offset-2 scale-105 shadow-lg" : "shadow-md hover:shadow-lg"
                      }`}
                    >
                      {shade?.shade?.detail?.shade_picture_url ? (
                        <img
                          src={shade?.shade.detail?.shade_picture_url}
                          alt={shade?.descr_shade}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <span className="text-gray-600 text-xs font-medium">{shade?.descr_shade}</span>
                      )}
                    </div>

                    <span className="mt-1 text-sm text-gray-700">{shade?.descr_shade}</span>
                  </div>
                );
              })}
              {showMore && filteredItemsList.length > visibleShades && (
                <button
                  onClick={() => setShadesModal(true)}
                  className={`w-16 h-16 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center hover:from-gray-200 hover:to-gray-300 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 border border-gray-300`}
                >
                  <span className="text-gray-600 font-semibold text-sm">+{filteredItemsList.length - visibleShades}</span>
                </button>
              )}
            </>
          ) : (
            <span className="text-black text-xs">Sorry! We couldn't find any matches.</span>
          )}
        </div>
      </>
    );
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Product Details");
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("", role_id) },
            {
              title: "Product Catalogue ",
              href: handleRouteNav("/product-catalog", role_id),
            },
            {
              title: "Product Details",
              href: handleRouteNav(`/product-details/${sort_no}`, role_id),
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href}>{item.title}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton]);

  const toggleFav = (e) => {
    const isFav = isPDSelected ? wishlistState.pd_tag === "yes" : wishlistState.td_tag === "yes";

    setWishlistState((prev) => ({
      pd_tag: isPDSelected ? (prev.pd_tag === "yes" ? "no" : "yes") : prev.pd_tag,
      td_tag: !isPDSelected ? (prev.td_tag === "yes" ? "no" : "yes") : prev.td_tag,
      pd_count: isPDSelected ? Math.max(0, Number(prev.pd_count || 0) + (prev.pd_tag === "yes" ? -1 : 1)) : prev.pd_count,
      td_count: !isPDSelected ? Math.max(0, Number(prev.td_count || 0) + (prev.td_tag === "yes" ? -1 : 1)) : prev.td_count,
    }));

    if (isFav) {
      rmoveWishlistItem(productData);
    } else {
      addWishlistItem(productData);
    }
  };

  const addWishlistItem = async (item) => {
    const payload = {
      customer_id: customer_id,
      product_id: item?.product?.product_id,
      sort_no: item?.product?.sort_no,
    };
    const res = await postApi("wishlist/add-wishlist", payload);
  };

  const rmoveWishlistItem = async (item) => {
    const payload = {
      customer_id: customer_id,
      product_id: item?.product?.product_id,
      sort_no: item?.product?.sort_no,
    };
    const res = await postApi("wishlist/delete-wishlist-product", payload);
  };

  return (
    <>
      <>
        <div className="product-details-container form-css max-w-[1200px] mx-auto p-6 overflow-x-hidden">
          <Row gutter={24} className="flex flex-wrap md:flex-nowrap">
            <Col xs={24} md={12} className="flex flex-col">
              <Card
                className="w-full mb-6 rounded-[24px] overflow-hidden relative"
                cover={
                  <ProductImageName
                    img_url={productData?.product?.product_pic_url}
                    img_alt={productData?.short_name}
                    parent_classname="h-[350px] object-cover border-b border-gray-200"
                    img_classname="w-full h-full object-cover rounded-xl"
                  />
                }
                bodyStyle={{ padding: 0 }}
              >
                {!is_customer && (
                  <div className="absolute top-4 left-4">
                    <Dropdown
                      menu={DropdownMenuItems({
                        item: productData,
                        fetchData: fetchProductData,
                        setCurrentItem,
                        setIsModalOpen,
                        navigate,
                        postApi,
                        role_id,
                        full_name,
                        user_id,
                        showPopup,
                        skipOptions: ["linkProducts"],
                      })}
                      trigger={["click"]}
                      overlayClassName="custom-dropdown-sales-menu"
                    >
                      <Typography.Link
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center" shape="circle" size="middle">
                          <TbDotsVertical className="text-red-700" size={18} />
                        </div>
                      </Typography.Link>
                    </Dropdown>
                  </div>
                )}

                {is_customer ? (
                  <div
                    className="absolute top-4 right-4 p-0 border border-transparent hover:border-[#A33434] transition-all duration-200"
                    style={{
                      position: "absolute",
                      width: 50,
                      height: 50,
                      borderRadius: "10px",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    }}
                    onClick={(e) => {
                      toggleFav(e);
                    }}
                  >
                    {(isPDSelected ? wishlistState.pd_tag === "yes" : wishlistState.td_tag === "yes") ? (
                      <svg className="cursor-pointer" width="26" height="22" viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M21 5.8125C21 12.375 11.2697 17.6869 10.8553 17.9062C10.7461 17.965 10.624 17.9958 10.5 17.9958C10.376 17.9958 10.2539 17.965 10.1447 17.9062C9.73031 17.6869 0 12.375 0 5.8125C0.00173694 4.27146 0.614681 2.79404 1.70436 1.70436C2.79404 0.614681 4.27146 0.00173694 5.8125 0C7.74844 0 9.44344 0.8325 10.5 2.23969C11.5566 0.8325 13.2516 0 15.1875 0C16.7285 0.00173694 18.206 0.614681 19.2956 1.70436C20.3853 2.79404 20.9983 4.27146 21 5.8125Z"
                          fill="#A33434"
                        />
                      </svg>
                    ) : (
                      <svg className="cursor-pointer" width="21" height="18" viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          opacity="0.1"
                          d="M21 5.8125C21 12.375 11.2697 17.6869 10.8553 17.9062C10.7461 17.965 10.624 17.9958 10.5 17.9958C10.376 17.9958 10.2539 17.965 10.1447 17.9062C9.73031 17.6869 0 12.375 0 5.8125C0.00173694 4.27146 0.614681 2.79404 1.70436 1.70436C2.79404 0.614681 4.27146 0.00173694 5.8125 0C7.74844 0 9.44344 0.8325 10.5 2.23969C11.5566 0.8325 13.2516 0 15.1875 0C16.7285 0.00173694 18.206 0.614681 19.2956 1.70436C20.3853 2.79404 20.9983 4.27146 21 5.8125Z"
                          fill="black"
                        />
                      </svg>
                    )}
                  </div>
                ) : (
                  <div className="absolute top-4 right-4 bg-opacity-90 rounded-full">
                    <Button
                      className="rounded-full text-2xl h-[40px] w-24"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(handleRouteNav("/product-catalog/favorites", role_id), {
                          state: {
                            sort_no: productData?.sort_no,
                            short_name: productData?.short_name,
                          },
                        });
                      }}
                    >
                      <FaHeart className="text-[#A33434] text-2xl" /> {isPDSelected ? wishlistState.pd_count : wishlistState.td_count || 0}
                    </Button>
                  </div>
                )}
              </Card>

              <div className="flex flex-col">
                <div className="flex flex-col sm:flex-row justify-between items-start font-abhaya gap-2 sm:gap-4">
                  <Title
                    level={2}
                    className="!mb-0 !text-left font-abhaya !font-normal truncate w-full sm:w-auto"
                    style={{
                      fontSize: "clamp(24px, 5vw, 40px)",
                      lineHeight: "90%",
                    }}
                    title={productData?.short_name}
                  >
                    {productData?.short_name}
                  </Title>

                  <div className="flex mt-2 sm:mt-0 items-start">
                    <CustomBadge variant={stockCalculations.stockStatus?.variant} text={stockCalculations.stockStatus?.text} />
                  </div>
                </div>

                <div className="flex flex-col">
                  <Text className="!text-left font-manrope !font-medium  !text-[24px]  !leading-[120%] !text-[#484848]">
                    {productData?.clienttype} {`${selectedTdData.tdlevelname || productData?.tdlevel_name}` || ""}
                  </Text>
                  {/* <div className="!text-left flex items-baseline"> */}
                  {/* <Title
                      level={3}
                      className="!mb-0 !inline-block !mr-1  !text-[35px]  !font-medium !font-manrope"
                    >
                      ₹{productData?.rate} / meter
                    </Title> */}
                  {/* </div> */}
                </div>
              </div>

              <div className="flex flex-wrap xl:flex-nowrap gap-4 mt-8 w-full">
                {filteredItemsList.length > 0 && (
                  <PrimaryButton
                    className="flex-1"
                    text={cart_id ? "Update to cart" : "Add to cart"}
                    onClick={handleAddToCart}
                    icon={cart_id ? <ReloadOutlined className="w-4 h-4 text-white" /> : <PlusOutlined className="w-4 h-4 text-white" />}
                    iconOnleft
                    loading={loading}
                    disabled={Number(noOfCases) < 1}
                  />
                )}

                {!cart_id && <PrimaryButton text={"Proceed to cart"} onClick={handleCheckout} noIcon className="flex-1" btnType="secondary" />}
              </div>
              <div className="max-w-2xl mx-auto pt-6 rounded-lg  mt-8">
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800">Disclaimers</h2>
                </div>

                <div className="space-y-4">
                  {disclaimerItems.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="w-2 h-2 bg-[#A33434] rounded-full"></div>
                      </div>
                      <p className="flex-1 text-left text-gray-600 leading-relaxed text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Col>

            <Divider type="vertical" className="mx-6 border-l-2 border-gray-300 h-auto" />

            <Col xs={24} md={12} className="flex flex-col gap-6 !pr-12 pb-4">
              <div>
                <Title level={5} className="!text-left !text-gray-800 !text-[35px] font-abhaya !mb-0">
                  Fabric Specifications
                </Title>
                <div className="grid grid-cols-1 col-wi">
                  <div className={`${is_customer ? " md:w-[88%]" : !collapsed ? "md:w-[88%]" : ""} flex flex-col gap-y-2 text-base`}>
                    <div className="flex justify-between">
                      <Text className="text-gray-500">Width:</Text>
                      <Text className="font-medium text-gray-900">
                        {Number(productData.finish_width) || 0}" inch / {inchesToCm(productData.finish_width)} cm
                      </Text>
                    </div>
                    <div className="flex justify-between">
                      <Text className="text-gray-500">Weight:</Text>
                      <Text className="font-medium text-gray-900">{productData.finish_wt || 0} GLM</Text>
                    </div>
                    <div className="flex justify-between">
                      <Text className="text-gray-500">Blend:</Text>
                      <Text className="font-medium text-gray-900">{productData.blend_name}</Text>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Text className="text-sm text-gray-500 block text-left mb-3  text-[20px]">Dyed type</Text>
                <div className="flex gap-3 w-full">
                  <Card
                    hoverable={pdItemsList.length > 0 || productData?.product?.sort_no_linked}
                    onClick={() => (pdItemsList.length === 0 && !productData?.product?.sort_no_linked ? null : onLinkedProductTap(true))}
                    className={`w-[50%] h-[70px] transition-all duration-200 rounded-[16px] ${
                      isPDSelected ? "!border-[2px] !border-[#A33434] bg-red-50 text-red-700 pointer-events-none" : "border-gray-200"
                    }  ${
                      pdItemsList.length === 0 && !productData?.product?.sort_no_linked
                        ? "opacity-50 cursor-not-allowed hover:!cursor-not-allowed hover:shadow-none"
                        : ""
                    }`}
                  >
                    <Text className="text-sm text-gray-500">Piece Dyed (PD)</Text>
                  </Card>
                  <Card
                    hoverable={tdItemsList.length > 0 || productData?.product?.sort_no_linked}
                    onClick={() => (tdItemsList.length === 0 && !productData?.product?.sort_no_linked ? null : onLinkedProductTap(false))}
                    className={`w-[50%] h-[70px] transition-all duration-200 rounded-[16px]  ${
                      !isPDSelected ? "!border-[2px] !border-[#A33434] bg-red-50 text-red-700 pointer-events-none" : "border-gray-200"
                    } ${
                      tdItemsList.length === 0 && !productData?.product?.sort_no_linked
                        ? "opacity-50 cursor-not-allowed hover:!cursor-not-allowed hover:shadow-none"
                        : ""
                    }`}
                  >
                    <Text className="text-sm text-gray-500">Top Dyed (TD)</Text>
                  </Card>
                </div>
              </div>

              <div className="">
                <Text className="text-sm text-gray-500 block text-left mb-3 text-[20px]">Availability</Text>
                <div className="flex gap-3 w-full flex-wrap">
                  {showInStockTab && (
                    <Card
                      hoverable
                      onClick={() => handleTabChange("in_stock")}
                      bodyStyle={{
                        padding: "12px 8px",
                        width: "100%",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      className={`flex-1 min-w-[100px] transition-all duration-200 rounded-[16px] border-[2px] ${
                        stockTab === "in_stock" ? "!border-[#A33434] bg-red-50 text-red-700 pointer-events-none" : "!border-white"
                      } max-w-[30%]  ${itemsList.length === 0 ? "opacity-50 cursor-not-allowed hover:!cursor-not-allowed hover:shadow-none" : ""}`}
                    >
                      <Text className="text-sm font-medium text-gray-500 whitespace-nowrap">In Stock</Text>
                    </Card>
                  )}

                  {isPDSelected && showGreyTab && (
                    <Card
                      hoverable
                      onClick={() => handleTabChange("grey")}
                      bodyStyle={{
                        padding: "12px 8px",
                        width: "100%",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      className={`flex-1 min-w-[100px] transition-all duration-200 rounded-[16px] border-[2px] ${
                        stockTab === "grey" ? "!border-[#A33434] bg-red-50 text-red-700 pointer-events-none" : "!border-white"
                      } max-w-[30%]  ${itemsList.length === 0 ? "opacity-50 cursor-not-allowed hover:!cursor-not-allowed hover:shadow-none" : ""}`}
                    >
                      <Text className="text-sm font-medium text-gray-500 whitespace-nowrap">Grey Available</Text>
                    </Card>
                  )}

                  <Card
                    hoverable
                    onClick={() => (itemsList.length > 0 ? handleTabChange("mto") : null)}
                    bodyStyle={{
                      padding: "12px 8px",
                      width: "100%",
                      textAlign: "center",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    className={`flex-1 min-w-[110px] transition-all duration-200 rounded-[16px] border-[2px] ${
                      stockTab === "mto" ? "!border-[#A33434] bg-red-50 text-red-700 pointer-events-none" : "!border-white"
                    } max-w-[30%]  ${itemsList.length === 0 ? "opacity-50 cursor-not-allowed hover:!cursor-not-allowed hover:shadow-none" : ""}`}
                  >
                    <Text className="text-sm font-medium text-gray-500 whitespace-nowrap">Make to order</Text>
                  </Card>
                </div>
              </div>

              <div>
                <Text className="text-sm text-gray-500 block mb-4 text-left text-[20px]">Shade</Text>
                <div className="w-full flex flex-wrap gap-3">{shadeChips()}</div>
              </div>

              {filteredItemsList.length > 0 && (
                <>
                  <div>
                    <Text className="text-sm text-gray-500 mb-3 flex w-full justify-between text-[20px]">
                      <span>Quantity</span>
                      <b>{stockTab === "grey" ? `Total Grey Available: ${stockCalculations.totalGreyQty || 0} m` : ""}</b>
                    </Text>
                    <div className={`${is_customer ? " " : !collapsed ? "" : ""} flex items-center justify-between gap-2`}>
                      <div className="flex items-center justify-between gap-x-2">
                        <Button
                          shape="circle"
                          onClick={minusNoOfCases}
                          disabled={noOfCases <= (isPDSelected ? 2 : 1)}
                          icon={<MinusOutlined className="!text-2xl" />}
                          className={`!w-[50px] !h-[50px] !min-w-[50px] flex items-center justify-center !text-2xl ${
                            noOfCases <= (isPDSelected ? 2 : 1)
                              ? "!border-gray-200 !text-gray-300 !cursor-not-allowed"
                              : "!border-gray-300 !text-gray-600 hover:!text-[#A33434] hover:!border-[#A33434]"
                          }`}
                        />

                        <div className="bg-white h-[50px] w-[150px] rounded-full border border-gray-200 flex items-center justify-center shadow-sm px-3">
                          <div className="flex items-center justify-center space-x-1">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={noOfCases}
                              onBlur={(e) => {
                                const value = e.target.value;
                                setNoOfCases(clampAndNormalizeCases(value));
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === "") {
                                  setNoOfCases("");
                                  return;
                                }
                                if (!/^\d+$/.test(raw)) return;
                                setNoOfCases(raw);
                              }}
                              className="bg-transparent outline-none text-gray-700 font-medium text-[18px] text-center w-[40px] no-spinner"
                            />
                            <span className="text-gray-500 font-medium">cases</span>
                          </div>
                        </div>

                        <Button
                          shape="circle"
                          onClick={plusNoOfCases}
                          icon={<PlusOutlined className="!text-2xl" />}
                          className="!w-[50px] !h-[50px] !min-w-[50px] flex items-center justify-center
                                  !border-gray-300 !text-gray-600 !text-2xl
                                  hover:!text-[#A33434] hover:!border-[#A33434]"
                        />
                      </div>

                      <div className="flex justify-end">
                        <div className="bg-gray-200 flex items-center justify-center opacity-100 p-3 rounded-[12px]">
                          <span className="whitespace-nowrap font-manrope font-bold text-[18px] leading-[110%] text-center text-[#A33434]">
                            {stockCalculations.totalMtrs.toLocaleString()} m
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-50 rounded-full flex items-center justify-center">
                      <img src="/assets/icons/TruckTrailer.png" alt="Truck" className="w-4 h-4 object-contain" />
                    </div>
                    <div className="flex flex-col items-start">
                      <Text className="text-left text-gray-700">
                        <strong>Estimated Dispatch</strong> -{" "}
                        {stockTab === "in_stock"
                          ? estimatedDispatchTimes.in_stock
                          : stockTab === "grey"
                            ? estimatedDispatchTimes.grey
                            : isPDSelected
                              ? estimatedDispatchTimes.make_to_order_pd
                              : estimatedDispatchTimes.make_to_order_td}
                      </Text>
                    </div>
                  </div>

                  {["grey", "mto"].includes(stockTab) && (
                    <div className="text-start space-y-4">
                      <Text className="text-sm text-gray-500 block text-left text-[20px]">APC</Text>
                      <div className="flex gap-3 w-full">
                        <Card
                          hoverable
                          onClick={() => setApcChecked(false)}
                          bodyStyle={{
                            padding: "12px 8px",
                            width: "100%",
                            textAlign: "center",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                          className={`flex-1 min-w-[80px] transition-all duration-200 rounded-[16px] border-[2px] ${
                            !apcChecked ? "!border-[#A33434] bg-red-50 text-red-700 pointer-events-none" : "!border-white"
                          }`}
                        >
                          <Text className="text-sm font-medium text-gray-500 whitespace-nowrap">No</Text>
                        </Card>
                        <Card
                          hoverable
                          onClick={() => {
                            setApcChecked(true);
                            setApcModal(true);
                          }}
                          bodyStyle={{
                            padding: "12px 8px",
                            width: "100%",
                            textAlign: "center",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                          className={`flex-1 min-w-[80px] transition-all duration-200 rounded-[16px] border-[2px] ${
                            apcChecked ? "!border-[#A33434] bg-red-50 text-red-700" : "!border-white"
                          }`}
                        >
                          <Text className="text-sm font-medium text-gray-500 whitespace-nowrap">Yes</Text>
                        </Card>
                      </div>
                      <div className="flex flex-col items-start">
                        <Text className="text-left text-gray-700">Please mention any additional notes regarding APC under Comments below.</Text>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col">
                    <Text className="text-sm text-gray-500 block mb-3 text-left">Comments</Text>
                    <TextArea
                      maxLength={100}
                      value={comments}
                      onChange={(e) => {
                        const cleanValue = e.target.value.replace(/[\n\r\t]/g, "");
                        setComments(cleanValue);
                      }}
                      placeholder="Write any additional notes here..."
                      rows={4}
                      classNames="w-full border border-gray-300 rounded-lg placeholder-gray-400 text-gray-700 shadow-sm bg-transparent !hover:border-gray-300 !focus:border-gray-300 !focus:shadow-none !active:border-gray-300 !outline-none"
                      style={{
                        boxShadow: "none",
                      }}
                    />
                  </div>

                  {Object.keys(tdMapping).length > 0 && selectedTdData.tdlevelcode && (
                    <div className="text-start space-y-4">
                      <Text className="text-sm text-gray-500 block text-left text-[20px]">Trade Level</Text>
                      <div className="flex gap-3 w-full flex-wrap">
                        {productData?.tdlevelcode_options?.split(",")?.map((code, index) => {
                          const tdCount = Object.keys(tdMapping).length;
                          const cardWidthClass =
                            tdCount === 1
                              ? "w-full"
                              : tdCount === 2
                                ? "flex-1 min-w-[80px]"
                                : tdCount === 3
                                  ? "flex-1 min-w-[70px]"
                                  : "flex-1 min-w-[60px]";
                          const isSelected = `${selectedTdData.tdlevelcode}` === `${code}`;

                          return (
                            <Card
                              key={code}
                              hoverable
                              onClick={() => setSelectedTdData({ tdlevelcode: code, tdlevelname: tdMapping[code] })}
                              bodyStyle={{
                                padding: "12px 8px",
                                width: "100%",
                                textAlign: "center",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                              className={`${cardWidthClass} transition-all duration-200 rounded-[16px] border-[2px] ${
                                isSelected ? "!border-[#A33434] bg-red-50 text-red-700 pointer-events-none" : "!border-white"
                              }`}
                            >
                              <Text className="text-sm font-medium text-gray-500 whitespace-nowrap">{tdMapping[code]}</Text>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>
        </div>
      </>

      <Modal
        className="flex flex-col items-center justify-center form-css-5 full-width-modal"
        open={orderSplitModal}
        footer={null}
        onCancel={() => setOrderSplitModal(false)}
        width={450}
      >
        <Title className="input-css font-abhaya !mb-0" level={2}>
          Important Information!
        </Title>
        <Title className=" !text-gray-500 !font-normal" level={5}>
          {is_customer ? "Your" : "This"} order will be divided into two parts.
        </Title>

        <div className="flex gap-3 justify-center items-center mt-4">
          <div className="w-[40px] h-[40px] bg-[#A334341A] rounded-full flex items-center justify-center shrink-0">
            <img src="/assets/icons/TruckTrailer.png" alt="Cube" className="w-auto h-[20px] object-contain" />
          </div>

          <div className="flex flex-col text-start">
            <span className="">
              {is_customer
                ? "For dispatch information please contact your sales representative."
                : "For dispatch information please inform clients to contact their sales representative."}
            </span>
          </div>
        </div>

        <div className="w-full rounded-2xl mt-5 border p-3">
          <div className="flex items-center gap-4">
            <div className="w-[50px] h-[50px] flex justify-center items-center">
              <img src={modalData?.shade?.shade?.detail?.shade_picture_url} alt={productData.short_name} className="w-[50px] h-[50px]" />
            </div>
            <div className="flex flex-col justify-start text-start">
              <p className="text-xl font-bold break-words whitespace-normal">{productData.short_name}</p>
              <p className="font-semibold text-gray-500 text-lg">
                {/* ₹{Number(productData.rate || 0).toLocaleString("en-IN")} / meter */}
                {modalData?.shade?.descr_shade}
                {stockTab === "grey" && showAPC(apcChecked)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 font-medium leading-6 gap-4 my-3">
            <div className="text-left leading-6 space-y-1">
              <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                {stockTab === "grey" ? "Grey Available" : "In Stock"}
                <CustomDot color={stockTab === "grey" ? badgeVariantStyles.greyStock.dotColor : badgeVariantStyles.success.dotColor} />
              </div>
              <p className="text-base leading-6 font-medium text-gray-900">
                {modalData.casesInStock || 0} cases (
                {roundDecimal(stockCalculations.inStockQty + stockCalculations.foldingQty + stockCalculations.greyQty)} m)
              </p>
            </div>
            <div className={`text-right leading-6 space-y-1`}>
              <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                Make to order
                <CustomDot color={badgeVariantStyles.makeToOrder.dotColor} />
              </div>
              <p className="text-base leading-6 font-medium text-gray-900">
                {modalData.makeToOrder || 0} cases (
                {roundDecimal(
                  stockCalculations.totalMtrs - (stockCalculations.inStockQty + stockCalculations.foldingQty + stockCalculations.greyQty),
                )}{" "}
                m)
              </p>
            </div>
          </div>
        </div>

        <div className="flex mt-8 w-full justify-center">
          <PrimaryButton
            text={cart_id ? "Update to cart" : "Add to cart"}
            disabled={Number(noOfCases) < 1}
            icon={cart_id ? <ReloadOutlined className="w-4 h-4 text-white" /> : <PlusOutlined className="w-4 h-4 text-white" />}
            onClick={(e) => handleAddToCart(e, true)}
            iconOnleft
            loading={loading}
          />
        </div>
      </Modal>

      <Modal
        className="items-center justify-center form-css-5 !w-[500px]"
        open={shadesModal}
        footer={null}
        onCancel={() => setShadesModal(false)}
        width={450}
      >
        <Title className="font-abhaya !mb-8" level={2}>
          {isPDSelected ? `${DYED_TYPES.pieceLabel} (PD) Shades` : `${DYED_TYPES.topLabel} (TD) Shades`}
        </Title>
        <SearchInput value={searchValue} onChange={(e) => setSearchValue(e.target.value)} className="mb-6" placeholder="Search for shades" />
        <div className="px-4">{shadeChips(false, searchValue)}</div>
      </Modal>

      <Modal
        className="flex flex-col items-center justify-center form-css-5 full-width-modal"
        open={apcModal}
        footer={null}
        onCancel={() => {
          setApcModal(false);
        }}
        width={450}
      >
        <Title className="input-css font-abhaya !mb-0" level={2}>
          Reminder!
        </Title>

        <div className="flex gap-3 justify-center items-center mt-4">
          <div className="flex flex-col text-start">
            <span className="text-[14px]">
              {is_customer ? "Please coordinate with your " : "Please inform clients to coordinate with their "}
              sales representative and provide sample cutting for APC requirement.
            </span>
          </div>
        </div>
      </Modal>

      <Modal
        className="flex flex-col items-center justify-center form-css-5 full-width-modal"
        open={quantityWarningModal}
        footer={null}
        onCancel={() => {
          setQuantityWarningModal(false);
        }}
        width={450}
      >
        <Title className="input-css font-abhaya !mb-0" level={2}>
          Alert!
        </Title>

        <div className="flex gap-3 justify-center items-center mt-4">
          <div className="flex flex-col text-start">
            <span className="text-[14px]">The quantity you are trying to order is too less. Please add more cases.</span>
          </div>
        </div>
      </Modal>

      <FileUploadModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onUpload={handleUpload}
        title="Upload Image"
        fileTypes={[".jpg", ".jpeg", ".png"]}
        buttonText="Update Image"
        fileTypeLabel="JPG or PNG"
      />
    </>
  );
};

export default ProductDetails;
